import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import PermissionSimulator from './PermissionSimulator.tsx';
import { 
  Users, 
  Clock, 
  FileText, 
  Building2, 
  TrendingUp, 
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Zap,
  Activity,
  Calendar,
  Bell,
  Star,
  ArrowRight
} from 'lucide-react';

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    setIsLoaded(true);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getGreetingEmoji = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '🌅';
    if (hour < 18) return '☀️';
    return '🌙';
  };

  const getRoleDashboard = () => {
    if (!currentUser?.permissions) {
      return <DefaultDashboard />;
    }

    const permissions = currentUser.permissions;
    
    // Admin gets admin dashboard
    if (permissions.includes('admin')) {
      return <AdminDashboard />;
    }
    
    // Users with timesheet or invoice management permissions get office staff dashboard
    if (permissions.includes('view_timesheets') || permissions.includes('view_invoices') || permissions.includes('manage_employees')) {
      return <OfficeStaffDashboard />;
    }
    
    // Field electricians get field dashboard
    if (permissions.includes('submit_timesheets')) {
      return <FieldElectricianDashboard />;
    }
    
    // Contractors get contractor dashboard
    if (permissions.includes('view_own_invoices')) {
      return <ContractorDashboard />;
    }
    
    return <DefaultDashboard />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <PermissionSimulator />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Enhanced Header */}
        <div className={`mb-8 ${isLoaded ? 'animate-fade-in' : 'opacity-0'}`}>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <div className="flex items-center space-x-3 mb-2">
                <span className="text-4xl">{getGreetingEmoji()}</span>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-800 bg-clip-text text-transparent">
                  {getGreeting()}, {currentUser?.displayName?.split(' ')[0] || 'User'}!
                </h1>
              </div>
              <p className="text-lg text-gray-600">
                Welcome to your 7Twenty4 Services dashboard
              </p>
              <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                <div className="flex items-center space-x-1">
                  <Calendar className="w-4 h-4" />
                  <span>{currentTime.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>{currentTime.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}</span>
                </div>
              </div>
            </div>
            
            {/* Quick Stats Bar */}
            <div className="flex items-center space-x-4">
              <div className="glass-card px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full status-dot"></div>
                  <span className="text-sm font-medium text-gray-700">System Online</span>
                </div>
              </div>
              <div className="glass-card px-4 py-2 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Activity className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">All Services Active</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Role-specific dashboard content */}
        <div className={`${isLoaded ? 'animate-slide-up' : 'opacity-0'}`}>
          {getRoleDashboard()}
        </div>
      </div>
    </div>
  );
};

const AdminDashboard: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Quick Actions */}
      <div className="glass-card rounded-xl shadow-soft border border-white/20 p-8 animate-fade-in">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickActionCard
            title="Manage Employees"
            description="View and manage employee records"
            icon={Users}
            href="/employees"
          />
          <QuickActionCard
            title="Process Timesheets"
            description="Review and approve pending timesheets"
            icon={Clock}
            href="/timesheets"
          />
          <QuickActionCard
            title="Generate Invoices"
            description="Create invoices for approved timesheets"
            icon={FileText}
            href="/invoices"
          />
        </div>
      </div>

      {/* Timesheet Management */}
      <div className="glass-card rounded-xl shadow-soft border border-white/20 p-8 animate-slide-up">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Timesheet Management</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TimesheetActionButton
            title="Single Entry"
            description="Create individual timesheet"
            href="/timesheets/entry"
            color="blue"
          />
          <TimesheetActionButton
            title="Bulk Entry"
            description="Create multiple timesheets"
            href="/timesheets/bulk"
            color="purple"
          />
          <TimesheetActionButton
            title="Processor"
            description="Process timesheet data"
            href="/timesheets/processor"
            color="green"
          />
          <TimesheetActionButton
            title="View All"
            description="Manage all timesheets"
            href="/timesheets"
            color="gray"
          />
        </div>
      </div>

      {/* System Status */}
      <div className="glass-card rounded-xl shadow-soft border border-white/20 p-8 animate-slide-up">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">System Status</h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl hover-lift">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              </div>
              <div>
                <span className="font-semibold text-green-900">System Online</span>
                <p className="text-sm text-green-700">All services operational</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm font-medium text-green-800">99.9% Uptime</div>
              <div className="text-xs text-green-600">Last 30 days</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const OfficeStaffDashboard: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Pending Timesheets"
          value="12"
          icon={Clock}
          color="orange"
          trend="Need approval"
        />
        <StatCard
          title="Invoices This Week"
          value="8"
          icon={FileText}
          color="green"
          trend="$12,450 total"
        />
        <StatCard
          title="License Expirations"
          value="3"
          icon={AlertTriangle}
          color="red"
          trend="Next 30 days"
        />
      </div>

      {/* Today's Tasks */}
      <div className="glass-card rounded-xl shadow-soft border border-white/20 p-8 animate-fade-in">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
            <Bell className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Today's Tasks</h2>
        </div>
        <div className="space-y-4">
          <TaskItem task="Review CEC timesheet submissions" priority="high" />
          <TaskItem task="Send license expiration notices" priority="medium" />
          <TaskItem task="Process payroll for week ending 1/26" priority="high" />
          <TaskItem task="Update employee documentation" priority="low" />
        </div>
      </div>

      {/* Timesheet Management */}
      <div className="glass-card rounded-xl shadow-soft border border-white/20 p-8 animate-slide-up">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Timesheet Management</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <TimesheetActionButton
            title="Single Entry"
            description="Create individual timesheet"
            href="/timesheets/entry"
            color="blue"
          />
          <TimesheetActionButton
            title="Bulk Entry"
            description="Create multiple timesheets"
            href="/timesheets/bulk"
            color="purple"
          />
          <TimesheetActionButton
            title="Processor"
            description="Process timesheet data"
            href="/timesheets/processor"
            color="green"
          />
          <TimesheetActionButton
            title="View All"
            description="Manage all timesheets"
            href="/timesheets"
            color="gray"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="glass-card rounded-xl shadow-soft border border-white/20 p-8 animate-slide-up">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900">Quick Actions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <QuickActionCard
            title="Process Timesheets"
            description="Review and approve pending timesheets"
            icon={Clock}
            href="/timesheets"
          />
          <QuickActionCard
            title="Employee Management"
            description="Update employee records and documentation"
            icon={Users}
            href="/employees"
          />
        </div>
      </div>
    </div>
  );
};

const FieldElectricianDashboard: React.FC = () => {
  const navigate = useNavigate();
  
  return (
    <div className="space-y-8">
      {/* Personal Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Hours This Week"
          value="42"
          icon={Clock}
          color="blue"
          trend="2 hours overtime"
        />
        <StatCard
          title="Current Rate"
          value="$28/hr"
          icon={DollarSign}
          color="green"
          trend="CEC project"
        />
        <StatCard
          title="License Status"
          value="Valid"
          icon={CheckCircle}
          color="green"
          trend="Expires 04/14/2026"
        />
      </div>

      {/* Personal Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Timesheet Management</h3>
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-gray-600 mb-4">Manage employee timesheets and time tracking</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => navigate('/timesheets/entry')}
                  className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  Single Entry
                </button>
                <button
                  onClick={() => navigate('/timesheets/bulk')}
                  className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                >
                  Bulk Entry
                </button>
                <button
                  onClick={() => navigate('/timesheets/processor')}
                  className="bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm"
                >
                  Processor
                </button>
                <button
                  onClick={() => navigate('/timesheets')}
                  className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  View All
                </button>
              </div>
            </div>

      {/* Recent Timesheets */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Timesheets</h2>
        <div className="space-y-3">
          <TimesheetRow week="Week ending 01/26/2025" hours="40" status="approved" />
          <TimesheetRow week="Week ending 01/19/2025" hours="42" status="approved" />
          <TimesheetRow week="Week ending 01/12/2025" hours="38" status="approved" />
        </div>
      </div>
    </div>
  );
};

const ContractorDashboard: React.FC = () => {
  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="This Month's Invoices"
          value="4"
          icon={FileText}
          color="blue"
          trend="$18,450 total"
        />
        <StatCard
          title="Outstanding Balance"
          value="$5,230"
          icon={DollarSign}
          color="orange"
          trend="2 invoices pending"
        />
        <StatCard
          title="Active Workers"
          value="12"
          icon={Users}
          color="green"
          trend="This week"
        />
      </div>

      {/* Recent Invoices */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Invoices</h2>
        <div className="space-y-3">
          <InvoiceRow invoice="INV-2025-001" date="01/26/2025" amount="$4,230" status="sent" />
          <InvoiceRow invoice="INV-2025-002" date="01/19/2025" amount="$3,850" status="paid" />
          <InvoiceRow invoice="INV-2025-003" date="01/12/2025" amount="$5,120" status="paid" />
        </div>
      </div>

      {/* Submit Timesheet */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Submit Weekly Timesheet</h2>
        <p className="text-gray-600 mb-4">Upload your weekly timesheet for processing</p>
        <button className="bg-brand-gold text-brand-dark px-6 py-2 rounded-lg font-medium hover:bg-yellow-500 transition-colors duration-200">
          Upload Timesheet
        </button>
      </div>
    </div>
  );
};

const DefaultDashboard: React.FC = () => {
  return (
    <div className="text-center py-12">
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Welcome to 7Twenty4 Services</h2>
      <p className="text-gray-600">Your dashboard is being set up. Please contact your administrator.</p>
    </div>
  );
};

// Helper Components
interface StatCardProps {
  title: string;
  value: string;
  icon: React.ComponentType<any>;
  color: 'blue' | 'green' | 'yellow' | 'purple' | 'orange' | 'red';
  trend?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color, trend }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    yellow: 'from-yellow-500 to-yellow-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
  };

  const bgColorClasses = {
    blue: 'from-blue-50 to-blue-100',
    green: 'from-green-50 to-green-100',
    yellow: 'from-yellow-50 to-yellow-100',
    purple: 'from-purple-50 to-purple-100',
    orange: 'from-orange-50 to-orange-100',
    red: 'from-red-50 to-red-100',
  };

  return (
    <div className={`glass-card rounded-xl shadow-soft border border-white/20 p-6 hover-lift animate-bounce-in bg-gradient-to-br ${bgColorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
          {trend && (
            <div className="flex items-center space-x-1">
              <TrendingUp className="w-4 h-4 text-gray-500" />
              <p className="text-sm text-gray-600">{trend}</p>
            </div>
          )}
        </div>
        <div className={`p-4 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg hover-scale`}>
          <Icon className="w-7 h-7 text-white" />
        </div>
      </div>
    </div>
  );
};

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  href: string;
}

const QuickActionCard: React.FC<QuickActionCardProps> = ({ title, description, icon: Icon, href }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // For the Generate Invoices button, navigate to the main invoices page
    if (href === '/invoices/generate') {
      navigate('/invoices');
    } else {
      navigate(href);
    }
  };

  return (
    <button
      onClick={handleClick}
      className="group block w-full text-left p-6 glass-card rounded-xl border border-white/20 hover:border-brand-gold/50 hover:shadow-glow transition-all duration-300 hover-lift"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-gradient-to-br from-brand-gold to-yellow-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-200">
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 group-hover:text-brand-gold transition-colors duration-200">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-brand-gold group-hover:translate-x-1 transition-all duration-200" />
      </div>
    </button>
  );
};

const AlertsSection: React.FC = () => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">System Alerts</h2>
      <div className="space-y-3">
        <div className="flex items-center space-x-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <div>
            <p className="font-medium text-red-900">3 licenses expiring soon</p>
            <p className="text-sm text-red-700">Review employee documentation</p>
          </div>
        </div>
        <div className="flex items-center space-x-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <Clock className="w-5 h-5 text-yellow-500" />
          <div>
            <p className="font-medium text-yellow-900">12 timesheets pending approval</p>
            <p className="text-sm text-yellow-700">Review and approve for payroll processing</p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface TaskItemProps {
  task: string;
  priority: 'high' | 'medium' | 'low';
}

const TaskItem: React.FC<TaskItemProps> = ({ task, priority }) => {
  const priorityColors = {
    high: 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border-red-300',
    medium: 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border-yellow-300',
    low: 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border-green-300',
  };

  const priorityIcons = {
    high: '🔥',
    medium: '⚡',
    low: '📝',
  };

  return (
    <div className="flex items-center justify-between p-4 glass-card rounded-xl border border-white/20 hover-lift transition-all duration-200">
      <div className="flex items-center space-x-3">
        <span className="text-lg">{priorityIcons[priority]}</span>
        <span className="text-gray-900 font-medium">{task}</span>
      </div>
      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${priorityColors[priority]}`}>
        {priority.toUpperCase()}
      </span>
    </div>
  );
};

interface DocumentStatusProps {
  name: string;
  status: 'valid' | 'expiring' | 'expired';
  expires: string;
}

const DocumentStatus: React.FC<DocumentStatusProps> = ({ name, status, expires }) => {
  const statusColors = {
    valid: 'text-green-600',
    expiring: 'text-yellow-600',
    expired: 'text-red-600',
  };

  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-gray-900">{name}</span>
      <div className="text-right">
        <p className={`text-xs font-medium ${statusColors[status]}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </p>
        <p className="text-xs text-gray-500">Expires {expires}</p>
      </div>
    </div>
  );
};

interface TimesheetRowProps {
  week: string;
  hours: string;
  status: 'approved' | 'pending' | 'rejected';
}

const TimesheetRow: React.FC<TimesheetRowProps> = ({ week, hours, status }) => {
  const statusColors = {
    approved: 'bg-green-100 text-green-800',
    pending: 'bg-yellow-100 text-yellow-800',
    rejected: 'bg-red-100 text-red-800',
  };

  return (
    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{week}</p>
        <p className="text-sm text-gray-600">{hours} hours</p>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    </div>
  );
};

interface InvoiceRowProps {
  invoice: string;
  date: string;
  amount: string;
  status: 'sent' | 'paid' | 'overdue';
}

const InvoiceRow: React.FC<InvoiceRowProps> = ({ invoice, date, amount, status }) => {
  const statusColors = {
    sent: 'bg-blue-100 text-blue-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
  };

  return (
    <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
      <div>
        <p className="font-medium text-gray-900">{invoice}</p>
        <p className="text-sm text-gray-600">{date}</p>
      </div>
      <div className="text-right">
        <p className="font-medium text-gray-900">{amount}</p>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status]}`}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      </div>
    </div>
  );
};

interface TimesheetActionButtonProps {
  title: string;
  description: string;
  href: string;
  color: 'blue' | 'purple' | 'green' | 'gray';
}

const TimesheetActionButton: React.FC<TimesheetActionButtonProps> = ({ title, description, href, color }) => {
  const navigate = useNavigate();

  const colorClasses = {
    blue: 'bg-blue-600 hover:bg-blue-700 text-white',
    purple: 'bg-purple-600 hover:bg-purple-700 text-white',
    green: 'bg-green-600 hover:bg-green-700 text-white',
    gray: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
  };

  return (
    <button
      onClick={() => navigate(href)}
      className={`p-4 rounded-lg transition-colors text-left ${colorClasses[color]}`}
    >
      <div className="font-medium text-sm mb-1">{title}</div>
      <div className="text-xs opacity-90">{description}</div>
    </button>
  );
};

export default Dashboard;
